import React, { useMemo, useState } from "react";
import { Box, IconButton, Divider, Tooltip } from "@mui/material";
import SortIcon from "@mui/icons-material/Sort";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ComplianceChart from "./ComplianceChart";
import DashboardCard from "../components/DashboardCard";
import { useAccessibleUserAccounts } from "../../../components/Common/HelperComponents/CustomHooks";
import { useOrganizations } from "../../../components/Common/HelperComponents/GetDataFromStore";
import { useComplianceData } from "./useComplianceData";
import ApiLoading from "../components/ApiLoading";
import ApiError from "../components/ApiError";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { NextPlanOutlined } from "@mui/icons-material";
import { getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";
import { getAccountCloudType } from "../../../CloudProviderContext";

// Industry-standard color mapping for compliance frameworks
const complianceColors = {
  "ISO27001-2022": "#003366", // ISO Blue
  "SOC2": "#6D6E71", // SOC2 Gray
  "CIS-5.0": "#00ADEF", // CIS Blue
  "PCI-DSS-4.0": "#009CDE", // PCI Blue
  "MITRE-ATTACK": "#FF6F00", // MITRE Orange
  "NIST-CSF-1.1": "#4B9CD3", // NIST Blue
  "NIST-800-171-Revision-2": "#4B9CD3", // NIST Blue
  "FedRamp-Moderate-Revision-4": "#B22234", // FedRAMP Red
  "AWS-Well-Architected-Framework-Security-Pillar": "#FF9900", // AWS Orange
  "HIPAA": "#2E74B5", // HIPAA Blue
  "ENS RD2022": "#E94E1B", // ENS Red
  "CIS-4.0.1": "#00ADEF", // CIS Blue
  "GDPR": "#003399", // GDPR Blue
  // Additional mappings for other compliance frameworks
  "AWS Well Architected Framework": "#FF9900", // AWS Orange
  "CCPA": "#E81E63", // CCPA Pink
  "CIS AWS Foundations Benchmark": "#00ADEF", // CIS Blue
  "CIS AWS Foundations Benchmark v3.0.0": "#00ADEF", // CIS Blue
  "CSA CCM": "#499894", // CSA Green
  "CSA CCM v4.02": "#499894", // CSA Green
  "EU GDPR": "#003399", // GDPR Blue
  "EU GDPR 2016/679": "#003399", // GDPR Blue
  "US HIPAA 164": "#2E74B5", // HIPAA Blue
  "HITRUST CSF": "#7D4F50", // HITRUST Brown
  "HITRUST CSF 11.2": "#7D4F50", // HITRUST Brown
  "ISO IEC 27001": "#003366", // ISO Blue
  "ISO IEC 27001:2002": "#003366", // ISO Blue
  "MITRE ATT&CK Cloud": "#FF6F00", // MITRE Orange
  "MITRE ATT&CK Containers": "#FF6F00", // MITRE Orange
  "NIST CSF": "#4B9CD3", // NIST Blue
  "NIST SP 800-53": "#4B9CD3", // NIST Blue
  "NIST SP 800-171": "#4B9CD3", // NIST Blue
  "PCI DSS": "#009CDE", // PCI Blue
  "PCI DSS v4.0": "#009CDE", // PCI Blue
  "AICPA SOC 2": "#6D6E71", // SOC2 Gray
};

const getColorForCompliance = (name) => {
  return complianceColors[name] || "#666666"; // Default gray if no match
};

const Compliance = ({ accountTypeFilter, sx }) => {
  const history = useHistory();
  const accessibleAccounts = useAccessibleUserAccounts();
  const organizations = useOrganizations();

  const [sortOrder, setSortOrder] = useState("desc");

  const accountsForMetrics = useMemo(() => {
    if (!accountTypeFilter) return accessibleAccounts;
    return accessibleAccounts.filter(
      (account) => getAccountCloudType(account) === accountTypeFilter
    );
  }, [accessibleAccounts, accountTypeFilter]);

  const accCodes = useMemo(
    () => accountsForMetrics.map((account) => account.accountCode),
    [accountsForMetrics]
  );
  const orgCode = useMemo(() => organizations?.[0]?.orgcode, [organizations]);

  const { data, loading, error } = useComplianceData(accCodes, orgCode);

  const sortedData = useMemo(() => {
    return data?.Compliance?.sort((a, b) =>
      sortOrder === "asc"
        ? a.Percentage - b.Percentage
        : b.Percentage - a.Percentage
    );
  }, [data, sortOrder]);

  const handleGoTo = () => {
    console.log("Go-to action clicked");
    history.push("/compliance/");
  };

  const actions = [
    {
      icon: (
        <Tooltip title="Open Detailed View">
          <IconButton onClick={handleGoTo} size="small" sx={{ p: 0, color: "text.secondary", transform: "rotate(-45deg)" }}>
            <NextPlanOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      label: "Go-to",
    },
  ];

  return (
    <DashboardCard title="Compliance" actions={actions} sx={sx}>
      {loading ? (
        <ApiLoading parameter="Compliance" />
      ) : error ? (
        <ApiError parameter="Compliance" />
      ) : (
        <Box
          sx={{
            width: "100%",
            flexGrow: 1,
            overflowY: "auto",
            pr: 1,
            mb: 1,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "background.default",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "grey.400",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "grey.500",
            },
          }}
        >
          {sortedData?.map((item, index) => (
            <ComplianceChart
              key={index}
              name={item.Name}
              value={item.Percentage}
              color={getColorForCompliance(item.Name)}
              icon={getComplianceIcon(item.Name)}
            />
          ))}
        </Box>
      )}
    </DashboardCard>
  );
};

export default Compliance;
